import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import { PersistConfig } from 'redux-persist/es/types';
import storage from 'redux-persist/lib/storage';
import { resetState } from '../../../store/actions';
import { RootState } from '../../../store/store';
import { closeAllModals } from '../../ui/store/ui-slice';
import { AuthCredentials } from '../models/auth-credentials';

export interface AuthState {
  isAuthenticated: boolean;
  domain: string | null;
  isDomainPrefilled: boolean;
  codeVerifier: string | null;
  authCredentials: AuthCredentials | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  domain: null,
  isDomainPrefilled: false,
  codeVerifier: null,
  authCredentials: null,
};

export const logout = createAsyncThunk('auth/logout', (payload, thunkAPI) => {
  const { isAuthenticated } = (thunkAPI.getState() as { auth: AuthState }).auth;

  thunkAPI.dispatch(resetState());
  thunkAPI.dispatch(closeAllModals());

  if (isAuthenticated) {
    return fetch(`${process.env.REACT_APP_API_URL}/portal-api/v1/user/logout`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(() => undefined)
      .catch(() => undefined);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticate: state => {
      state.isAuthenticated = true;
    },
    setDomain: (state, { payload }: PayloadAction<string>) => {
      state.domain = payload;
    },
    setIsDomainPrefilled: (state, { payload }: PayloadAction<boolean>) => {
      state.isDomainPrefilled = payload;
    },
    setCodeVerifier: (state, { payload }: PayloadAction<string>) => {
      state.codeVerifier = payload;
    },
    setAuthCredentials: (state, { payload }: PayloadAction<AuthCredentials>) => {
      state.authCredentials = payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(resetState.match, () => initialState);
  },
});

export const selectDomain = (state: RootState) => state.auth.domain;
export const selectPrefilledDomain = (state: RootState) => (state.auth.isDomainPrefilled ? state.auth.domain : null);
export const selectCodeVerifier = (state: RootState) => state.auth.codeVerifier;
export const selectAuthCredentials = (state: RootState) => state.auth.authCredentials;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;

export const { setDomain, setIsDomainPrefilled, setCodeVerifier, setAuthCredentials, authenticate } = authSlice.actions;

const persistConfig: PersistConfig<AuthState> = {
  key: authSlice.name,
  storage: storage,
  version: 1,
  whitelist: ['isAuthenticated', 'domain', 'isDomainPrefilled', 'codeVerifier', 'authCredentials'] as Array<
    keyof AuthState
  >,
};

const authReducer = persistReducer(persistConfig, authSlice.reducer);

export default authReducer;
